import { Controller } from "@hotwired/stimulus"

// Flags a region with data-overflowing while something inside it is scrolled
// out of sight, so chrome that only earns its place over hidden content — a
// footer rule, a shadow — can key off it in CSS.
//
//   <div class="peer overflow-y-auto" data-controller="scroll-overflow">…</div>
//   <div class="peer-data-[overflowing]:border-t">…</div>
//
// A region that hands scrolling to a child (a pinned search box above a
// scrolling list, say) names that child instead:
//
//   <div data-scroll-overflow-target="scroller" class="overflow-y-auto">…</div>
export default class extends Controller {
  static targets = ["scroller"]

  // The rule this flags is itself a pixel tall, and it steals that pixel from
  // the scroller. Without the slack, content overflowing by exactly 1px would
  // flip the flag on and off forever. A thicker rule needs a matching slack.
  static values = { slack: { type: Number, default: 1 } }

  connect() {
    this.sync = this.sync.bind(this)

    this.resizeObserver = new ResizeObserver(this.sync)
    this.mutationObserver = new MutationObserver((records) => {
      if (records.some(record => this.scrollers.includes(record.target))) {
        this.observeContents()
      } else {
        this.sync()
      }
    })
    this.mutationObserver.observe(this.element, { childList: true, subtree: true })

    this.observeContents()
  }

  disconnect() {
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
  }

  get scrollers() {
    return this.hasScrollerTarget ? this.scrollerTargets : [this.element]
  }

  observeContents() {
    this.resizeObserver.disconnect()

    this.scrollers.forEach(scroller => {
      this.resizeObserver.observe(scroller)
      Array.from(scroller.children).forEach(child => this.resizeObserver.observe(child))
    })

    this.sync()
  }

  sync() {
    const overflowing = this.scrollers.some(
      scroller => scroller.scrollHeight > scroller.clientHeight + this.slackValue
    )

    this.element.toggleAttribute("data-overflowing", overflowing)
  }
}
